import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";

const AdminPanel = () => {
  const [applications, setApplications] = useState([]);
  const [expandedApplication, setExpandedApplication] = useState(null);
  const navigate = useNavigate();
  const { logout } = useAuth();

  useEffect(() => {
    fetchApplications();
  }, []);

  const fetchApplications = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/admin/applications`,
        {
          credentials: "include",
        }
      );
      if (response.ok) {
        const data = await response.json();
        setApplications(data);
        // console.log(data);
      } else {
        console.error("Failed to fetch applications");
      }
    } catch (error) {
      console.error("Error fetching applications:", error);
    }
  };

  const handleApprove = async (id) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/admin/approve/${id}`,
        {
          method: "POST",
          credentials: "include",
        }
      );
      if (response.ok) {
        fetchApplications(); // Refresh the list
      } else {
        console.error("Failed to approve application");
      }
    } catch (error) {
      console.error("Error approving application:", error);
    }
  };

  const handleReject = async (id) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/admin/reject/${id}`,
        {
          method: "POST",
          credentials: "include",
        }
      );
      if (response.ok) {
        fetchApplications(); // Refresh the list
      } else {
        console.error("Failed to reject application");
      }
    } catch (error) {
      console.error("Error rejecting application:", error);
    }
  };

  const toggleExpand = (id) => {
    setExpandedApplication(expandedApplication === id ? null : id);
  };

  const handleLogout = async () => {
    await logout();
    navigate("/", { replace: true });
  };

  const handleArticleApp = () => {
    navigate("/article-application");
  };

  const statusColor = (status) => {
    if (status === 0) return "bg-[#feca57] text-black"; // pending
    if (status === 1) return "bg-[#ff9ff3] text-black"; // approved
    return "bg-[#ff6b6b] text-white"; // rejected
  };

  return (
    <div className="admin-panel text-[#333]">
      <header className="admin-header flex justify-between items-center p-5 bg-[#f8f8f8] shadow-[0_2px_4px_rgba(0,0,0,0.1)] max-[768px]:flex-col max-[768px]:items-start">
        <img src="/logo.png" alt="Sports Hub Logo" className="logo h-10" />
        <div className="header-buttons flex items-center gap-3">
          <button
            onClick={handleArticleApp}
            className="article-app-btn bg-[#3498db] text-white py-2 px-5 rounded-[5px] transition-colors duration-300 hover:bg-[#2980b9]"
          >
            Article Applications
          </button>
          <button
            onClick={handleLogout}
            className="logout-btn bg-[#ffc107] text-white py-2 px-5 rounded-[5px] transition-colors duration-300 hover:bg-[#ff6b81] max-[768px]:mt-2.5"
          >
            Logout
          </button>
        </div>
      </header>
      <main className="admin-main p-5">
        <h1 className="text-[#2f3542] mb-5 text-2xl">Contributor Applications</h1>
        <div className="table-container overflow-x-auto">
          <table className="w-full border-collapse bg-white shadow-[0_0_20px_rgba(0,0,0,0.1)]">
            <thead>
              <tr>
                <th className="bg-[#5352ed] text-white font-bold p-[15px] text-left">Name</th>
                <th className="bg-[#5352ed] text-white font-bold p-[15px] text-left">Email</th>
                <th className="bg-[#5352ed] text-white font-bold p-[15px] text-left">Description</th>
                <th className="bg-[#5352ed] text-white font-bold p-[15px] text-left">Status</th>
                <th className="bg-[#5352ed] text-white font-bold p-[15px] text-left">Actions</th>
              </tr>
            </thead>
            <tbody>
              {applications.map((app) => (
                <React.Fragment key={app.contributor_id}>
                  <tr
                    onClick={() => toggleExpand(app.contributor_id)}
                    className="hover:bg-[#f5f6fa] border-b border-[#ddd]"
                  >
                    <td className="p-[15px] text-left">{app.full_name}</td>
                    <td className="p-[15px] text-left">{app.email}</td>
                    <td className="p-[15px] text-left">{app.description}</td>
                    <td className="p-[15px] text-left">
                      <span
                        className={`status inline-block px-2 py-1 rounded-full text-[0.8em] font-bold ${statusColor(
                          app.status
                        )}`}
                      >
                        {app.status === 0
                          ? "Pending"
                          : app.status === 1
                          ? "Approved"
                          : "Rejected"}
                      </span>
                    </td>
                    <td className="p-[15px] text-left">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleApprove(app.contributor_id);
                        }}
                        className="approve-btn bg-[#1dd1a1] text-white py-2 px-4 rounded-[5px] mr-2 transition-colors duration-300 hover:bg-[#10ac84]"
                      >
                        Approve
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleReject(app.contributor_id);
                        }}
                        className="reject-btn bg-[#ff6b6b] text-white py-2 px-4 rounded-[5px] transition-colors duration-300 hover:bg-[#ee5253]"
                      >
                        Reject
                      </button>
                    </td>
                  </tr>
                  {expandedApplication === app.id && (
                    <tr className="border-b border-[#ddd]">
                      <td colSpan="5" className="p-[15px] text-left">
                        <div className="expanded-details bg-[#f1f2f6] p-5 rounded-[5px] mt-2">
                          <h3 className="font-semibold mb-2">Detailed Information</h3>
                          <p>
                            <strong>Description:</strong> {app.description}
                          </p>
                          <p>
                            <strong>Personal Website:</strong> {app.personalWebsite}
                          </p>
                          <h4 className="font-semibold mt-3">Experiences:</h4>
                          {app.experiences.map((exp, index) => (
                            <div key={index} className="experience bg-white p-[15px] mb-2.5 rounded-[5px] shadow-[0_2px_4px_rgba(0,0,0,0.1)]">
                              <p>
                                <strong>Title:</strong> {exp.title}
                              </p>
                              <p>
                                <strong>Genre:</strong> {exp.genre}
                              </p>
                              <p>
                                <strong>Publisher:</strong> {exp.publisher}
                              </p>
                              <p>
                                <strong>Date:</strong> {exp.date}
                              </p>
                              <p>
                                <strong>URL:</strong> {exp.url}
                              </p>
                            </div>
                          ))}
                          <h4 className="font-semibold mt-3">Social Media:</h4>
                          <p>
                            <strong>Twitter:</strong> {app.twitter}
                          </p>
                          <p>
                            <strong>Instagram:</strong> {app.instagram}
                          </p>
                          <p>
                            <strong>LinkedIn:</strong> {app.linkedin}
                          </p>
                          <p>
                            <strong>Facebook:</strong> {app.facebook}
                          </p>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </main>
    </div>
  );
};

export default AdminPanel;
